/**
 * ----------------------------------------------------------------------------------------------------------------------
 * LOOK ITEM — ajout sur mesure Leo & Ugo (ne fait pas partie du thème Prestige)
 * ----------------------------------------------------------------------------------------------------------------------
 *
 * Sélecteur de variante compact utilisé dans le tiroir « Voir le look » (snippets/look-drawer.liquid). Il résout la
 * variante à partir des options cochées, tient à jour le prix, le coloris affiché, la vignette et l'état du bouton.
 * L'ajout au panier lui-même reste géré par le composant <product-form> du thème.
 *
 * Les données du produit sont lues dans le <script type="application/json" data-look-data> rendu par le snippet :
 *   { options: [{ param, name, values: [{ id, name }] }], variants: [{ id, options, available, price, compare_at, image }] }
 */
if (!window.customElements.get('look-item')) {
  class LookItem extends HTMLElement {
    connectedCallback() {
      const dataScript = this.querySelector('[data-look-data]');

      if (!dataScript) {
        return;
      }

      try {
        this.productData = JSON.parse(dataScript.textContent);
      } catch (error) {
        return;
      }

      this.valueNames = {};

      this.productData.options.forEach((option) => {
        option.values.forEach((value) => {
          this.valueNames[value.id] = value.name;
        });
      });

      this.onOptionChanged = (event) => {
        this.update(this.productData.options.findIndex((option) => option.param === event.target.name));
      };

      this.addEventListener('change', this.onOptionChanged);
      this.update(null);
    }

    disconnectedCallback() {
      if (this.onOptionChanged) {
        this.removeEventListener('change', this.onOptionChanged);
      }
    }

    /**
     * Les noms des valeurs cochées, dans l'ordre des options du produit (null si l'option n'est pas choisie)
     */
    get selection() {
      return this.productData.options.map((option) => {
        const checked = this.querySelector(`input[name="${option.param}"]:checked`);

        return checked ? this.valueNames[checked.value] : null;
      });
    }

    findVariant(selection) {
      return this.productData.variants.find((variant) => {
        return selection.every((name, index) => name !== null && variant.options[index] === name);
      });
    }

    /**
     * Une valeur est proposable s'il existe une variante disponible qui la porte, en respectant les options
     * précédemment choisies. On ne filtre que sur les options qui précèdent, pour ne jamais enfermer la cliente
     * dans une combinaison sans issue (c'est aussi le comportement du sélecteur du thème).
     */
    isValueAvailable(optionIndex, valueName, selection) {
      return this.productData.variants.some((variant) => {
        if (variant.available === false || variant.options[optionIndex] !== valueName) {
          return false;
        }

        for (let index = 0; index < optionIndex; index++) {
          if (selection[index] !== null && variant.options[index] !== selection[index]) {
            return false;
          }
        }

        return true;
      });
    }

    /**
     * @param {number|null} changedOptionIndex l'index de l'option que la cliente vient de changer, null au premier rendu
     */
    update(changedOptionIndex) {
      const selection = this.selection;
      const variant = this.findVariant(selection);
      const lastIndex = this.productData.options.length - 1;

      // Changer de coloris peut rendre la taille cochée introuvable : on bascule alors sur la première taille
      // disponible. En revanche, si c'est la taille elle-même qui vient d'être choisie, on respecte ce choix et
      // le bouton annonce la rupture plutôt que de revenir en douce sur une autre taille.
      const sizeWasNotChosen = changedOptionIndex === null || changedOptionIndex < lastIndex;

      if (lastIndex > 0 && sizeWasNotChosen && (!variant || variant.available === false)) {
        const lastOption = this.productData.options[lastIndex];
        const fallback = lastOption.values.find((value) => this.isValueAvailable(lastIndex, value.name, selection));

        if (fallback) {
          const input = this.querySelector(`input[name="${lastOption.param}"][value="${fallback.id}"]`);

          if (input && !input.checked) {
            input.checked = true;
            return this.update(lastIndex);
          }
        }
      }

      this.updateOptionStates(selection);
      this.updateSelectedValues(selection);
      this.updatePrice(variant);
      this.updateImage(variant);
      this.updateButton(variant);
    }

    updateOptionStates(selection) {
      this.productData.options.forEach((option, optionIndex) => {
        option.values.forEach((value) => {
          const input = this.querySelector(`input[name="${option.param}"][value="${value.id}"]`);
          const label = input && input.labels ? input.labels[0] : null;

          if (label) {
            label.classList.toggle('is-disabled', this.isValueAvailable(optionIndex, value.name, selection) === false);
          }
        });
      });
    }

    updateSelectedValues(selection) {
      this.productData.options.forEach((option, index) => {
        const output = this.querySelector(`[data-look-selected-value="${option.param}"]`);

        if (output) {
          output.textContent = selection[index] || '';
        }
      });
    }

    updatePrice(variant) {
      const price = this.querySelector('[data-look-price]');
      const compareAt = this.querySelector('[data-look-compare-at]');

      if (price && variant) {
        price.textContent = variant.price;
      }

      if (compareAt) {
        compareAt.textContent = variant && variant.compare_at ? variant.compare_at : '';
        compareAt.hidden = !(variant && variant.compare_at);
      }
    }

    updateImage(variant) {
      const image = this.querySelector('[data-look-image] img');

      if (image && variant && variant.image && image.getAttribute('src') !== variant.image) {
        image.setAttribute('src', variant.image);
        image.removeAttribute('srcset');
      }
    }

    updateButton(variant) {
      const button = this.querySelector('[data-look-submit]');
      const idInput = this.querySelector('input[name="id"]');

      if (!button || !idInput) {
        return;
      }

      const isBuyable = Boolean(variant) && variant.available !== false;

      if (isBuyable) {
        idInput.value = variant.id;
      }

      button.disabled = isBuyable === false;
      button.textContent = isBuyable
        ? this.getAttribute('add-to-cart-label')
        : this.getAttribute('sold-out-label');
    }
  }

  window.customElements.define('look-item', LookItem);
}

/**
 * Le panier s'ouvre en panneau latéral : on referme le tiroir du look dès qu'un produit y est ajouté, pour ne pas
 * laisser deux panneaux superposés (c'est ce que fait déjà la modale d'achat rapide du thème).
 *
 * Deux signaux, parce qu'il y a deux paniers possibles :
 *   - « variant:add », l'événement du thème, émis par <product-form> ;
 *   - l'ouverture du panneau de l'app UpCart, qui est le panier réellement utilisé sur cette boutique. UpCart
 *     intercepte l'envoi du formulaire et n'émet aucun événement DOM : il faut donc observer son panneau.
 * Si UpCart venait à être désinstallé, le premier signal prend le relais sans rien changer ici.
 */
function hideOpenLookDrawers() {
  document.querySelectorAll('.look-drawer[open]').forEach((drawer) => {
    if (typeof drawer.hide === 'function') {
      drawer.hide();
    }
  });
}

document.addEventListener('variant:add', hideOpenLookDrawers);

function watchCartPopup() {
  const cartPopup = document.getElementById('UpcartPopup');

  if (!cartPopup) {
    return false;
  }

  new MutationObserver(() => {
    if (cartPopup.classList.contains('upcartPopupShow')) {
      hideOpenLookDrawers();
    }
  }).observe(cartPopup, { attributes: true, attributeFilter: ['class'] });

  return true;
}

if (!watchCartPopup()) {
  // Le panneau est injecté par l'app, souvent après le chargement du thème
  const bodyObserver = new MutationObserver(() => {
    if (watchCartPopup()) {
      bodyObserver.disconnect();
    }
  });

  bodyObserver.observe(document.body, { childList: true });
}
